import React, { useState, useEffect, useRef } from 'react';
import { Modal, Form, Button, Row, Col, Badge, Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes, faEdit, faTrash, faMinus, faPlus, faPrint, faEllipsisV, faCheck, faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import PatternLock from '../shared/PatternLock';
import DatePicker from 'react-datepicker';
import { it } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import { formatDate, parseDate, getDatePickerLocale } from '../../utils/dateUtils';
import { cacheService } from '../../services/cacheService';
import { retryWithBackoff } from '../../utils/retryUtils';
import { differenceInDays } from 'date-fns';
import settingsService from '../../services/settingsService';
import { PROBLEM_OPTIONS } from '../../constants';
import { inventoryService } from '../../services/firestore';
import { printReceipt } from '../../services/printService';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../utils/formatCurrency';
import './DeviceDetails.scss';

const STATUS_OPTIONS = [
  { value: 'inRiparazione', label: 'In Riparazione', color: 'warning' },
  { value: 'riparato', label: 'Riparato', color: 'info' },
  { value: 'restituito', label: 'Restituito', color: 'success' },
  { value: 'ordine', label: 'Ordine', color: 'primary' },
  { value: 'nonRiparabile', label: 'Non Riparabile', color: 'secondary' }
];

const PatternDisplay = ({ pattern }) => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    if (!pattern?.length) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    canvas.width = 200;
    canvas.height = 200;
    
    const padding = 20;
    const radius = 8;
    const gridSize = 3;
    const cellSize = (canvas.width - (padding * 2)) / (gridSize - 1);
    
    // Crea la griglia di punti
    const points = [];
    for (let i = 0; i < gridSize; i++) {
      for (let j = 0; j < gridSize; j++) {
        points.push({
          x: padding + j * cellSize,
          y: padding + i * cellSize,
          index: i * gridSize + j
        });
      }
    }

    // Disegna i punti e il pattern
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Disegna le linee del pattern
    if (pattern.length > 0) {
      ctx.beginPath();
      ctx.strokeStyle = '#007bff';
      ctx.lineWidth = 2;
      pattern.forEach((pointIndex, i) => {
        const point = points.find(p => p.index === pointIndex);
        if (i === 0) {
          ctx.moveTo(point.x, point.y);
        } else {
          ctx.lineTo(point.x, point.y);
        }
      });
      ctx.stroke();
    }

    // Disegna i punti
    points.forEach(point => {
      ctx.beginPath();
      ctx.arc(point.x, point.y, radius, 0, Math.PI * 2);
      ctx.fillStyle = pattern.includes(point.index) ? '#007bff' : '#666';
      ctx.fill();
    });
  }, [pattern]);

  return (
    <canvas
      ref={canvasRef}
      className="pattern-lock-canvas"
      style={{ 
        maxWidth: '200px',
        maxHeight: '200px',
        pointerEvents: 'none' // Disabilita le interazioni
      }}
    />
  );
};

// Crea un componente personalizzato per il toggle del dropdown
const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
  <Button
    ref={ref}
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
    variant="light"
    className="btn-icon"
  >
    <FontAwesomeIcon icon={faEllipsisV} />
  </Button>
));

// Crea un nuovo CustomToggleRight per il menu di destra
const CustomToggleRight = React.forwardRef(({ children, onClick }, ref) => (
  <Button
    ref={ref}
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
    variant="light"
    className="btn-icon px-4 border border-success"
  >
    <FontAwesomeIcon icon={faCircleCheck} className="text-success" />
  </Button>
));

function DeviceDetails({ device, show, onHide, onUpdate, onDelete }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [formData, setFormData] = useState(null);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [pendingChanges, setPendingChanges] = useState(false);
  const [warrantyDays, setWarrantyDays] = useState(null);
  const [warrantyStatus, setWarrantyStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPrinting, setIsPrinting] = useState(false);
  const [currency, setCurrency] = useState('EUR');

  const PROBLEM_OPTIONS = [
    { value: 'Schermo', label: t('models.device.problems.Schermo') },
    { value: 'Batteria', label: t('models.device.problems.Batteria') },
    { value: 'Software', label: t('models.device.problems.Software') },
    { value: 'Connettore', label: t('models.device.problems.Connettore') },
    { value: 'Fotocamera', label: t('models.device.problems.Fotocamera') },
    { value: 'Autoparlante', label: t('models.device.problems.Autoparlante') },
    { value: 'Cassa', label: t('models.device.problems.Cassa') },
    { value: 'Microfono', label: t('models.device.problems.Microfono') },
    { value: 'Acqua', label: t('models.device.problems.Acqua') },
    { value: 'NonSiAccende', label: t('models.device.problems.NonSiAccende') },
    { value: 'Reset', label: t('models.device.problems.Reset') },
    { value: 'Altro', label: t('models.device.problems.Altro') }
  ];

  useEffect(() => {
    const loadInitialData = async () => {
      if (!show) return;
      
      setIsLoading(true);
      try {
        const { data: settings } = await settingsService.get();
        const warrantyDaysValue = settings.warrantyDays || 0;
        setWarrantyDays(warrantyDaysValue);
        setCurrency(settings.currency || 'EUR');
        
        const deliveryDate = parseDate(device.deliveryDate);
        
        // Prepara i dati elaborando i problemi personalizzati
        const problems = device.problems || [device.problem || ''];
        const customProblems = Array(problems.length).fill('');
        
        // Estrae problemi personalizzati dal formato "Altro:descrizione"
        const processedProblems = problems.map((prob, index) => {
          if (prob.startsWith('Altro:')) {
            customProblems[index] = prob.substring(6); // Elimina "Altro:" dal problema
            return 'Altro';
          }
          return prob;
        });
        
        const newFormData = { 
          ...device,
          problems: processedProblems,
          customProblems,
          deliveryDate,
          pattern: device.pattern || [],
          passcode: device.passcode || '',
          unlockType: device.unlockType || 'pattern'
        };
        setFormData(newFormData);

        // Calcola subito lo stato della garanzia
        if (deliveryDate && device.status === 'restituito') {
          const daysSinceDelivery = differenceInDays(new Date(), deliveryDate);
          setWarrantyStatus({
            isInWarranty: daysSinceDelivery <= warrantyDaysValue,
            label: daysSinceDelivery <= warrantyDaysValue ? t('deviceDetails.warranty.inWarranty') : t('deviceDetails.warranty.outOfWarranty'),
            color: daysSinceDelivery <= warrantyDaysValue ? 'success' : 'danger'
          });
        }
      } catch (error) {
        console.error('Error loading initial data:', error);
        setWarrantyDays(0);
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialData();
  }, [device, show]);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (!isOffline && pendingChanges) {
      syncPendingChanges();
    }
  }, [isOffline, pendingChanges]);

  const syncPendingChanges = async () => {
    try {
      const cachedChanges = await cacheService.get(`device_${device.id}_changes`);
      if (cachedChanges) {
        await retryWithBackoff(() => onUpdate(cachedChanges));
        await cacheService.remove(`device_${device.id}_changes`);
        setPendingChanges(false);
      }
    } catch (error) {
      console.error('Error syncing pending changes:', error);
    }
  };

  const handleEdit = () => {
    setEditing(true);
  };

  const handleSave = async () => {
    try {
      // Prepara i dati da salvare, combinando i problemi normali e personalizzati
      const deviceData = {
        ...formData,
        problems: formData.problems.map((problem, index) => {
          if (problem === 'Altro' && formData.customProblems && formData.customProblems[index]) {
            return 'Altro:' + formData.customProblems[index];
          }
          return problem;
        }),
        pattern: formData.pattern || [],
        passcode: formData.passcode || '',
        unlockType: formData.unlockType || 'pattern'
      };
      
      // Non inviare il campo customProblems al backend
      delete deviceData.customProblems;
      
      if (isOffline) {
        await cacheService.set(`device_${device.id}_changes`, deviceData);
        setPendingChanges(true);
        setEditing(false);
        return;
      }

      await retryWithBackoff(() => onUpdate(deviceData));
      setEditing(false);
    } catch (error) {
      console.error('Error updating device:', error);
      await cacheService.set(`device_${device.id}_changes`, formData);
      setPendingChanges(true);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Gestione speciale per i problemi
    if (name.startsWith('problem-')) {
      const index = parseInt(name.split('-')[1]);
      setFormData(prev => ({
        ...prev,
        problems: prev.problems.map((prob, i) => i === index ? value : prob)
      }));
      return;
    }
    
    // Gestione per i problemi personalizzati
    if (name.startsWith('customProblem-')) {
      const index = parseInt(name.split('-')[1]);
      const newCustomProblems = [...(formData.customProblems || Array(formData.problems.length).fill(''))];
      newCustomProblems[index] = value;
      
      setFormData(prev => ({
        ...prev,
        customProblems: newCustomProblems
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDateChange = (date, field) => {
    setFormData(prev => ({
      ...prev,
      [field]: date
    }));
  };

  const handleTimeChange = (value, type) => {
    if (!formData.deliveryDate) return;
    
    const newDate = new Date(formData.deliveryDate);
    
    if (type === 'hours') {
      newDate.setHours(parseInt(value));
    } else if (type === 'minutes') {
      newDate.setMinutes(parseInt(value));
    }
    
    handleDateChange(newDate, 'deliveryDate');
  };

  const handlePatternChange = (newPattern) => {
    setFormData(prev => ({
      ...prev,
      pattern: newPattern
    }));
  };

  const handleDelivery = async () => {
    try {
      // Prepara i dati da salvare preservando i problemi personalizzati
      const deviceData = {
        ...formData,
        problems: formData.problems.map((problem, index) => {
          if (problem === 'Altro' && formData.customProblems && formData.customProblems[index]) {
            return 'Altro:' + formData.customProblems[index];
          }
          return problem;
        }),
        status: 'restituito',
        paymentStatus: formData.status === 'nonRiparabile' ? 'nonRichiesto' : 'pagato',
        deliveryDate: new Date(),
        pattern: formData.pattern || [],
        passcode: formData.passcode || '',
        unlockType: formData.unlockType || 'pattern'
      };
      
      // Non inviare il campo customProblems al backend
      delete deviceData.customProblems;
      
      if (isOffline) {
        await cacheService.set(`device_${device.id}_changes`, deviceData);
        setPendingChanges(true);
      } else {
        await retryWithBackoff(() => onUpdate(deviceData));
      }
      
      onHide();
    } catch (error) {
      console.error('Error updating device status:', error);
      alert('Errore durante l\'aggiornamento dello stato. Riprova.');
    }
  };

  const handleDelete = async () => {
    try {
      await onDelete(device.id);
      setShowDeleteModal(false);
      onHide();
    } catch (error) {
      console.error('Error deleting device:', error);
    }
  };

  const handleRepaired = async () => {
    try {
      // Prepara i dati da salvare preservando i problemi personalizzati
      const deviceData = {
        ...formData,
        problems: formData.problems.map((problem, index) => {
          if (problem === 'Altro' && formData.customProblems && formData.customProblems[index]) {
            return 'Altro:' + formData.customProblems[index];
          }
          return problem;
        }),
        status: 'riparato',
        pattern: formData.pattern || [],
        passcode: formData.passcode || '',
        unlockType: formData.unlockType || 'pattern'
      };
      
      // Non inviare il campo customProblems al backend
      delete deviceData.customProblems;
      
      // Aggiorna l'inventario se necessario
      try {
        if (formData.partsUsed?.length > 0) {
          await inventoryService.updatePartsAfterRepair(formData.partsUsed);
        }
      } catch (error) {
        console.error('Error updating inventory:', error);
      }

      if (isOffline) {
        await cacheService.set(`device_${device.id}_changes`, deviceData);
        setPendingChanges(true);
        setFormData({
          ...deviceData,
          // Ricrea i customProblems per visualizzazione
          customProblems: formData.customProblems
        });
      } else {
        await retryWithBackoff(() => onUpdate(deviceData));
        setFormData({
          ...deviceData,
          // Ricrea i customProblems per visualizzazione
          customProblems: formData.customProblems
        });
      }
      onHide();
    } catch (error) {
      console.error('Error updating device status:', error);
      alert('Errore durante l\'aggiornamento dello stato. Riprova.');
    }
  };

  const handlePrint = async () => {
    try {
      setIsPrinting(true);
      const settings = await settingsService.get();
      
      // Prepara i dati del dispositivo con i problemi nel formato corretto per la stampa
      const deviceToPrint = {
        ...formData,
        // Combina problemi normali e personalizzati nel formato "Altro:descrizione"
        problems: formData.problems.map((problem, index) => {
          if (problem === 'Altro' && formData.customProblems && formData.customProblems[index]) {
            return 'Altro:' + formData.customProblems[index];
          }
          return problem;
        })
      };
      
      await printReceipt(deviceToPrint, {
        companyName: settings.data.companyName,
        region: settings.data.region,
        address: settings.data.address,
        phone: settings.data.phone,
        logo: settings.data.logo,
        warrantyDays: settings.data.warrantyDays,
        disclaimer: settings.data.disclaimer,
        receiptWidth: settings.data.receiptWidth
      });
    } catch (error) {
      console.error('Errore durante la stampa:', error);
      alert(t('deviceDetails.errors.print'));
    } finally {
      setIsPrinting(false);
    }
  };

  const handleNotRepairable = async () => {
    try {
      // Prepara i dati da salvare preservando i problemi personalizzati
      const deviceData = {
        ...formData,
        problems: formData.problems.map((problem, index) => {
          if (problem === 'Altro' && formData.customProblems && formData.customProblems[index]) {
            return 'Altro:' + formData.customProblems[index];
          }
          return problem;
        }),
        status: 'nonRiparabile',
        paymentStatus: 'nonRichiesto',
        pattern: formData.pattern || [],
        passcode: formData.passcode || '',
        unlockType: formData.unlockType || 'pattern'
      };
      
      // Non inviare il campo customProblems al backend
      delete deviceData.customProblems;
      
      if (isOffline) {
        await cacheService.set(`device_${device.id}_changes`, deviceData);
        setPendingChanges(true);
        setFormData({
          ...deviceData,
          // Ricrea i customProblems per visualizzazione
          customProblems: formData.customProblems
        });
      } else {
        await retryWithBackoff(() => onUpdate(deviceData));
        setFormData({
          ...deviceData,
          // Ricrea i customProblems per visualizzazione
          customProblems: formData.customProblems
        });
      }
      onHide();
    } catch (error) {
      console.error('Error updating device status:', error);
      alert('Errore durante l\'aggiornamento dello stato. Riprova.');
    }
  };

  const getStatusLabel = (status) => {
    return t(`models.device.status.${status}`);
  };

  const getPaymentStatusLabel = (status) => {
    return t(`models.device.paymentStatus.${status}`);
  };

  const getStatusBadgeColor = (status) => {
    const statusOption = STATUS_OPTIONS.find(opt => opt.value === status);
    return statusOption ? statusOption.color : 'secondary';
  };

  const getWarrantyStatus = () => {
    return warrantyStatus;
  };

  const formatDateWithOptionalTime = (date) => {
    if (!date) return '-';
    
    const parsedDate = parseDate(date);
    
    // Se il dispositivo è stato restituito, mostra solo la data
    if (formData.status === 'restituito') {
      return formatDate(date, "dd/MM/yyyy");
    }
    
    // Controlla se l'ora è diversa da 00:00
    const hasSpecificTime = parsedDate && (parsedDate.getHours() !== 0 || parsedDate.getMinutes() !== 0);
    
    return formatDate(date, hasSpecificTime ? "dd/MM/yyyy HH:mm" : "dd/MM/yyyy");
  };

  if (isLoading || !formData) {
    return null;
  }

  return (
    <>
      <Modal show={show && !isLoading} onHide={onHide} size="lg" className="device-details-modal">
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title>
            {t('deviceDetails.title')} #{device.repairNumber}
            <Badge 
              bg={getStatusBadgeColor(formData.status)}
              className="ms-2"
            >
              {getStatusLabel(formData.status)}
            </Badge>
            {warrantyStatus && formData.paymentStatus !== 'nonRichiesto' && (
              <Badge 
                bg={warrantyStatus.color}
                className="ms-2"
              >
                {warrantyStatus.label}
              </Badge>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-light p-4">
          <Form>
            <Row className="g-4">
              {/* Prima colonna */}
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('deviceDetails.fields.brand')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="brand"
                    value={formData.brand}
                    onChange={handleInputChange}
                    disabled={!editing}
                    className={!editing ? 'bg-light border' : ''}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>{t('deviceDetails.fields.model')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="model"
                    value={formData.model}
                    onChange={handleInputChange}
                    disabled={!editing}
                    className={!editing ? 'bg-light border' : ''}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>{t('deviceDetails.fields.serialNumber')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="serialNumber"
                    value={formData.serialNumber}
                    onChange={handleInputChange}
                    disabled={!editing}
                    className={!editing ? 'bg-light border' : ''}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>{t('deviceDetails.fields.customerPhone')}</Form.Label>
                  <Form.Control
                    type="tel"
                    name="customerPhone"
                    value={formData.customerPhone}
                    onChange={handleInputChange}
                    disabled={!editing}
                    className={!editing ? 'bg-light border' : ''}
                  />
                </Form.Group>
              </Col>

              {/* Seconda colonna */}
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('deviceDetails.fields.problems')}</Form.Label>
                  {editing ? (
                    formData.problems.map((problem, index) => (
                      <div key={index} className="mb-2">
                        <div className="d-flex mb-2">
                          <Form.Select
                            name={`problem-${index}`}
                            value={problem}
                            onChange={handleInputChange}
                            className="form-control me-2"
                          >
                            <option value="">{t('deviceForm.fields.selectProblem')}</option>
                            {PROBLEM_OPTIONS.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </Form.Select>
                          {index > 0 && (
                            <Button 
                              variant="outline-danger" 
                              size="sm"
                              onClick={() => {
                                const newProblems = formData.problems.filter((_, i) => i !== index);
                                const newCustomProblems = (formData.customProblems || []).filter((_, i) => i !== index);
                                setFormData(prev => ({ 
                                  ...prev, 
                                  problems: newProblems,
                                  customProblems: newCustomProblems
                                }));
                              }}
                              className="flex-shrink-0"
                            >
                              <FontAwesomeIcon icon={faMinus} />
                            </Button>
                          )}
                        </div>
                        {problem === 'Altro' && (
                          <Form.Control
                            type="text"
                            name={`customProblem-${index}`}
                            value={(formData.customProblems || [])[index] || ''}
                            onChange={handleInputChange}
                            placeholder={t('deviceForm.fields.customProblem') || "Specifica il problema"}
                            className="mb-2"
                          />
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="bg-light border rounded p-2">
                      {formData.problems.map((problem, index) => (
                        <div key={index} className="mb-1">
                          {problem === 'Altro' && formData.customProblems && formData.customProblems[index]
                            ? `${t(`models.device.problems.${problem}`)}: ${formData.customProblems[index]}`
                            : t(`models.device.problems.${problem}`)}
                        </div>
                      ))}
                    </div>
                  )}
                  {editing && (
                    <Button 
                      variant="outline-primary" 
                      size="sm" 
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          problems: [...prev.problems, ''],
                          customProblems: [...(prev.customProblems || []), '']
                        }));
                      }}
                      className="mt-2"
                    >
                      <FontAwesomeIcon icon={faPlus} className="me-2" />
                      {t('deviceDetails.buttons.addProblem')}
                    </Button>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>{t('deviceDetails.fields.notes')}</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    disabled={!editing}
                    className={!editing ? 'bg-light border' : ''}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>{t('deviceDetails.fields.price.repair')}</Form.Label>
                  <Form.Control
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    onWheel={(e) => e.currentTarget.blur()}
                    disabled={!editing}
                    className={!editing ? 'bg-light border' : ''}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>{t('deviceDetails.fields.price.deposit')}</Form.Label>
                  <Form.Control
                    type="number"
                    name="deposit"
                    value={formData.deposit}
                    onChange={handleInputChange}
                    onWheel={(e) => e.currentTarget.blur()}
                    disabled={!editing}
                    className={!editing ? 'bg-light border' : ''}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>{t('deviceDetails.fields.price.remaining')}</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.paymentStatus === 'pagato' ? 
                      formatCurrency(0, currency) : 
                      formatCurrency((formData.price || 0) - (formData.deposit || 0), currency)
                    }
                    readOnly
                    disabled
                    className="bg-light border"
                  />
                </Form.Group>
              </Col>

              {/* Terza colonna */}
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('deviceDetails.fields.dates.delivery')}</Form.Label>
                  {editing ? (
                    <div className="date-time-select-container">
                      <div className="date-picker-container">
                        <DatePicker
                          selected={formData.deliveryDate}
                          onChange={(date) => handleDateChange(date, 'deliveryDate')}
                          dateFormat="dd/MM/yyyy"
                          className="form-control"
                          isClearable
                          placeholderText={t('deviceDetails.fields.dates.selectDate')}
                          locale={getDatePickerLocale()}
                          popperClassName="datepicker-improved"
                        />
                      </div>
                      {formData.deliveryDate && (
                        <div className="time-select-container mt-2">
                          <Form.Label>{t('deviceDetails.fields.deliveryTime', 'Ora di consegna')}</Form.Label>
                          <div className="d-flex time-selectors">
                            <Form.Select
                              value={formData.deliveryDate ? formData.deliveryDate.getHours() : ""}
                              onChange={(e) => handleTimeChange(e.target.value, 'hours')}
                              className="time-select time-hours me-2"
                            >
                              <option value="" disabled>{t('deviceDetails.fields.hour', 'Ora')}</option>
                              {Array.from({ length: 24 }).map((_, i) => (
                                <option key={`hour-${i}`} value={i}>
                                  {i.toString().padStart(2, '0')}
                                </option>
                              ))}
                            </Form.Select>
                            
                            <Form.Select
                              value={formData.deliveryDate ? formData.deliveryDate.getMinutes() : ""}
                              onChange={(e) => handleTimeChange(e.target.value, 'minutes')}
                              className="time-select time-minutes"
                            >
                              <option value="" disabled>{t('deviceDetails.fields.minute', 'Minuti')}</option>
                              {[0, 15, 30, 45].map((minutes) => (
                                <option key={`min-${minutes}`} value={minutes}>
                                  {minutes.toString().padStart(2, '0')}
                                </option>
                              ))}
                            </Form.Select>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <Form.Control
                      type="text"
                      value={formatDateWithOptionalTime(formData.deliveryDate)}
                      readOnly
                      disabled
                      className="bg-light border"
                    />
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>{t('deviceDetails.fields.status.repair')}</Form.Label>
                  <Form.Select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    disabled={!editing}
                    className={!editing ? 'bg-light border' : ''}
                  >
                    {Object.keys(t('models.device.status', { returnObjects: true })).map(status => (
                      <option key={status} value={status}>
                        {t(`models.device.status.${status}`)}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>{t('deviceDetails.fields.status.payment')}</Form.Label>
                  <Form.Select
                    name="paymentStatus"
                    value={formData.paymentStatus}
                    onChange={handleInputChange}
                    disabled={!editing}
                    className={!editing ? 'bg-light border' : ''}
                  >
                    {Object.keys(t('models.device.paymentStatus', { returnObjects: true })).map(status => (
                      <option key={status} value={status}>
                        {t(`models.device.paymentStatus.${status}`)}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Segna come ordine"
                    name="isOrder"
                    checked={formData.isOrder}
                    onChange={handleInputChange}
                    disabled={!editing}
                    className={!editing ? 'text-muted' : ''}
                  />
                </Form.Group>

                <Form.Group className="mt-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <Form.Label className="mb-0">{t('deviceDetails.fields.unlockPattern.label', 'Codice di Sblocco')}</Form.Label>
                    {editing && (
                      <Form.Check
                        type="switch"
                        id="unlock-type-switch"
                        label={formData.unlockType === 'passcode' ? t('deviceDetails.fields.unlockPattern.passcode', 'Codice') : t('deviceDetails.fields.unlockPattern.pattern', 'Pattern')}
                        checked={formData.unlockType === 'passcode'}
                        onChange={() => {
                          setFormData(prev => ({
                            ...prev,
                            unlockType: prev.unlockType === 'passcode' ? 'pattern' : 'passcode',
                            pattern: prev.unlockType === 'passcode' ? prev.pattern : [],
                            passcode: prev.unlockType === 'pattern' ? prev.passcode : ''
                          }));
                        }}
                      />
                    )}
                  </div>
                  
                  {editing ? (
                    <>
                      {formData.unlockType === 'passcode' ? (
                        <Form.Control
                          type="text"
                          name="passcode"
                          value={formData.passcode || ''}
                          onChange={handleInputChange}
                          placeholder={t('deviceDetails.fields.unlockPattern.enterPasscode', 'Inserisci il codice di sblocco')}
                          className="mb-2"
                        />
                      ) : (
                        <>
                          <PatternLock
                            onComplete={handlePatternChange}
                            initialPattern={formData.pattern}
                          />
                          {formData.pattern?.length > 0 && (
                            <Button 
                              variant="outline-secondary" 
                              size="sm" 
                              className="mt-2"
                              onClick={() => handlePatternChange([])}
                            >
                              {t('deviceDetails.fields.unlockPattern.reset')}
                            </Button>
                          )}
                        </>
                      )}
                    </>
                  ) : (
                    <div className="pattern-display bg-light border rounded p-3">
                      {formData.unlockType === 'passcode' ? (
                        formData.passcode ? (
                          <div className="text-center">
                            {formData.passcode}
                          </div>
                        ) : (
                          <div className="text-center text-muted">
                            {t('deviceDetails.fields.unlockPattern.noPattern')}
                          </div>
                        )
                      ) : (
                        formData.pattern?.length > 0 ? (
                          <>
                            <PatternDisplay pattern={formData.pattern} />
                            <div className="text-muted text-center mt-2">
                              Pattern: {formData.pattern.join(' → ')}
                            </div>
                          </>
                        ) : (
                          <div className="text-center text-muted">
                            {t('deviceDetails.fields.unlockPattern.noPattern')}
                          </div>
                        )
                      )}
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-top">
          <div className="d-flex justify-content-between w-100">
            <div>
              {!editing ? (
                <Dropdown>
                  <Dropdown.Toggle as={CustomToggle} id="dropdown-actions" />
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={handleEdit}>
                      <FontAwesomeIcon icon={faEdit} className="me-2" />
                      {t('deviceDetails.buttons.edit')}
                    </Dropdown.Item>
                    <Dropdown.Item onClick={handlePrint} disabled={isPrinting}>
                      <FontAwesomeIcon 
                        icon={faPrint} 
                        className="me-2" 
                        spin={isPrinting}
                      />
                      {isPrinting ? t('deviceDetails.buttons.printing') : t('deviceDetails.buttons.print')}
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              ) : (
                <Button 
                  variant="danger" 
                  onClick={() => setShowDeleteModal(true)} 
                >
                  <FontAwesomeIcon icon={faTrash} className="me-2" />
                  {t('deviceDetails.buttons.delete')}
                </Button>
              )}
            </div>
            <div>
              {editing ? (
                <>
                  <Button variant="secondary" onClick={() => setEditing(false)} className="me-2">
                    <FontAwesomeIcon icon={faTimes} className="me-2" />
                    {t('deviceDetails.buttons.cancel')}
                  </Button>
                  <Button variant="success" onClick={handleSave}>
                    <FontAwesomeIcon icon={faSave} className="me-2" />
                    {t('deviceDetails.buttons.save')}
                  </Button>
                </>
              ) : formData.status !== 'restituito' && (
                <Dropdown>
                  <Dropdown.Toggle as={CustomToggleRight} id="dropdown-status-actions" />
                  <Dropdown.Menu align="end">
                    {formData.status !== 'riparato' && (
                      <Dropdown.Item onClick={handleRepaired} className="status-action-repaired">
                        <FontAwesomeIcon icon={faEdit} className="me-2" />
                        {t('deviceDetails.buttons.markAsRepaired')}
                      </Dropdown.Item>
                    )}
                    {formData.status !== 'nonRiparabile' && (
                      <Dropdown.Item onClick={handleNotRepairable} className="status-action-not-repairable">
                        <FontAwesomeIcon icon={faTimes} className="me-2" />
                        {t('deviceDetails.buttons.markAsNotRepairable')}
                      </Dropdown.Item>
                    )}
                    <Dropdown.Item onClick={handleDelivery} className="status-action-delivery">
                      <FontAwesomeIcon icon={faCheck} className="me-2" />
                      {t('deviceDetails.buttons.confirmDelivery')}
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              )}
            </div>
          </div>
        </Modal.Footer>
        {isOffline && (
          <div className="alert alert-warning m-3 mb-0 rounded-0">
            {t('deviceDetails.offline.message')}
            {pendingChanges && (
              <span className="ms-2">({t('deviceDetails.offline.pendingChanges')})</span>
            )}
          </div>
        )}
      </Modal>

      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
        size="sm"
      >
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title as="h5">{t('deviceDetails.deleteConfirm.title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          <FontAwesomeIcon 
            icon={faTrash} 
            className="text-danger mb-3" 
            style={{ fontSize: '2rem' }} 
          />
          <p className="mb-0">{t('deviceDetails.deleteConfirm.message')}</p>
          <p className="text-muted small mb-0">
            {t('deviceDetails.deleteConfirm.warning')}
          </p>
        </Modal.Body>
        <Modal.Footer className="border-top">
          <Button 
            variant="outline-secondary" 
            onClick={() => setShowDeleteModal(false)}
          >
            {t('deviceDetails.deleteConfirm.cancel')}
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDelete}
          >
            {t('deviceDetails.deleteConfirm.confirm')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default DeviceDetails; 